import { alpha, createTheme, type PaletteMode, type ThemeOptions } from '@mui/material/styles';
import { FONT_FAMILY, vyora } from './tokens';

const sharedTypography: ThemeOptions['typography'] = {
  fontFamily: FONT_FAMILY,
  fontWeightLight: 300,
  fontWeightRegular: 400,
  fontWeightMedium: 500,
  fontWeightBold: 700,
  h1: { fontWeight: 700, letterSpacing: '-0.025em', fontSize: '1.75rem', lineHeight: 1.25 },
  h2: { fontWeight: 700, letterSpacing: '-0.02em', fontSize: '1.45rem', lineHeight: 1.3 },
  h3: { fontWeight: 650, letterSpacing: '-0.015em', fontSize: '1.125rem', lineHeight: 1.35 },
  h4: {
    fontWeight: 650,
    letterSpacing: '-0.015em',
    fontSize: '1.125rem',
    lineHeight: 1.35,
    '@media (max-width:600px)': { fontSize: '1.05rem' },
  },
  h5: { fontWeight: 650, fontSize: '1.05rem', letterSpacing: '-0.01em' },
  h6: { fontWeight: 600, fontSize: '0.95rem', letterSpacing: '-0.01em' },
  subtitle1: { fontWeight: 600, fontSize: '0.95rem' },
  subtitle2: { fontWeight: 600, fontSize: '0.85rem' },
  body1: { fontSize: '1rem', lineHeight: 1.55, fontWeight: 400 },
  body2: { fontSize: '0.875rem', lineHeight: 1.5, fontWeight: 400 },
  caption: { fontSize: '0.8125rem', lineHeight: 1.45 },
  button: { textTransform: 'none', fontWeight: 650, letterSpacing: '0.01em', fontSize: '1rem' },
};

const sharedShape = { borderRadius: 8 };

const ease = 'cubic-bezier(0.22, 1, 0.36, 1)';

function componentOverrides(mode: PaletteMode): ThemeOptions['components'] {
  const isDark = mode === 'dark';
  const hairline = isDark ? vyora.hairlineDark : vyora.hairlineLight;
  const focusRing = isDark ? alpha(vyora.aqua, 0.22) : alpha(vyora.teal, 0.16);

  return {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          fontFamily: FONT_FAMILY,
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
          overflowX: 'clip',
          maxWidth: '100%',
        },
        body: {
          fontFamily: FONT_FAMILY,
          minHeight: '100vh',
          overflowX: 'clip',
          maxWidth: '100%',
          overscrollBehaviorX: 'none',
        },
        '#root': {
          maxWidth: '100%',
          minWidth: 0,
          overflowX: 'clip',
        },
        '.app-page': {
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
        },
        '.app-table-scroll': {
          overscrollBehaviorX: 'contain',
        },
        '::selection': {
          backgroundColor: isDark ? alpha(vyora.aqua, 0.32) : alpha(vyora.teal, 0.2),
        },
        ':focus-visible': {
          outline: `2px solid ${isDark ? vyora.aqua : vyora.teal}`,
          outlineOffset: 2,
        },
        '::-webkit-scrollbar': { width: 10, height: 10 },
        '::-webkit-scrollbar-thumb': {
          backgroundColor: isDark ? 'rgba(148,163,184,0.28)' : 'rgba(13,19,40,0.16)',
          borderRadius: 99,
          border: '2px solid transparent',
          backgroundClip: 'padding-box',
        },
        '@media (prefers-reduced-motion: reduce)': {
          '*, *::before, *::after': {
            animationDuration: '0.01ms !important',
            animationIterationCount: '1 !important',
            transitionDuration: '0.01ms !important',
            scrollBehavior: 'auto !important',
          },
        },
      },
    },
    MuiButtonBase: {
      defaultProps: { disableRipple: false },
      styleOverrides: {
        root: {
          transition: `background-color 180ms ${ease}, box-shadow 180ms ${ease}, color 180ms ${ease}, border-color 180ms ${ease}`,
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true, variant: 'contained' },
      styleOverrides: {
        root: {
          borderRadius: 8,
          minHeight: 40,
          paddingInline: 18,
          textTransform: 'none',
          fontWeight: 650,
          fontSize: '0.9375rem',
          whiteSpace: 'nowrap',
        },
        sizeSmall: { minHeight: 32, paddingInline: 12, fontSize: '0.8125rem', borderRadius: 6 },
        sizeLarge: { minHeight: 46, paddingInline: 22, fontSize: '1rem', borderRadius: 8 },
        containedPrimary: {
          backgroundColor: vyora.teal,
          '&:hover': {
            backgroundColor: vyora.tealDark,
            boxShadow: isDark
              ? `0 8px 20px ${alpha(vyora.aqua, 0.22)}`
              : `0 8px 20px ${alpha(vyora.teal, 0.28)}`,
          },
        },
        outlined: {
          borderColor: hairline,
          '&:hover': { borderColor: 'currentColor' },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          transition: `background-color 160ms ${ease}`,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
        rounded: { borderRadius: 8 },
        outlined: { borderColor: hairline, borderRadius: 8 },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          border: `1px solid ${hairline}`,
          boxShadow: isDark ? '0 8px 24px rgba(0,0,0,0.28)' : '0 2px 10px rgba(20, 33, 61, 0.04)',
          borderRadius: 4,
          transition: `box-shadow 220ms ${ease}, border-color 220ms ${ease}`,
          '&:hover': {
            boxShadow: isDark ? '0 10px 28px rgba(0,0,0,0.36)' : '0 6px 18px rgba(20, 33, 61, 0.07)',
          },
        },
      },
    },
    MuiCardActionArea: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          '&:hover .MuiCardActionArea-focusHighlight': { opacity: 0.04 },
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: 20,
          '&:last-child': { paddingBottom: 20 },
        },
      },
    },
    MuiFormControl: {
      defaultProps: {
        fullWidth: true,
      },
      styleOverrides: {
        root: {
          minWidth: 0,
          '& .MuiInputLabel-root': {
            zIndex: 2,
          },
        },
      },
    },
    MuiTextField: {
      defaultProps: { size: 'medium', variant: 'outlined', fullWidth: true },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontSize: '0.92rem',
          boxSizing: 'border-box',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : '#fff',
          transition: `box-shadow 180ms ${ease}, border-color 180ms ${ease}`,
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: isDark ? 'rgba(148,163,184,0.4)' : 'rgba(13,19,40,0.18)',
          },
          '&.Mui-focused': {
            boxShadow: `0 0 0 4px ${focusRing}`,
          },
          '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus, & input:-webkit-autofill:active': {
            WebkitBoxShadow: isDark
              ? '0 0 0 1000px #111827 inset !important'
              : '0 0 0 1000px #FFFFFF inset !important',
            WebkitTextFillColor: isDark ? '#F8FAFC !important' : '#0F172A !important',
            caretColor: isDark ? '#F8FAFC' : '#0F172A',
            borderRadius: '0 !important',
            transition: 'background-color 5000s ease-in-out 0s',
          },
        },
        input: {
          padding: '11px 14px',
          height: 'auto',
          fontSize: '0.92rem',
          lineHeight: '1.4375em',
          boxSizing: 'border-box',
          '&[type="date"], &[type="datetime-local"], &[type="time"]': {
            colorScheme: isDark ? 'dark' : 'light',
          },
          '&[type="date"]::-webkit-calendar-picker-indicator, &[type="datetime-local"]::-webkit-calendar-picker-indicator, &[type="time"]::-webkit-calendar-picker-indicator': {
            filter: isDark ? 'invert(1)' : 'none',
            cursor: 'pointer',
            opacity: 0.85,
          },
        },
        inputSizeSmall: {
          padding: '8px 12px',
          fontSize: '0.86rem',
        },
        multiline: {
          padding: '11px 14px',
        },
        notchedOutline: { borderColor: hairline },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          fontSize: '0.9rem',
          transform: 'translate(14px, 11px) scale(1)',
          '&.MuiInputLabel-sizeSmall': {
            transform: 'translate(12px, 8px) scale(1)',
            fontSize: '0.86rem',
          },
          '&.MuiInputLabel-shrink': {
            transform: 'translate(14px, -9px) scale(0.75)',
            backgroundColor: isDark ? '#141A2E' : '#FFFFFF',
            padding: '0 5px',
            borderRadius: '3px',
            zIndex: 2,
          },
          '&.MuiInputLabel-shrink.MuiInputLabel-sizeSmall': {
            transform: 'translate(12px, -8px) scale(0.75)',
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          marginLeft: 4,
          marginTop: 4,
          fontSize: '0.75rem',
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        MenuProps: {
          disableScrollLock: false,
          PaperProps: {
            sx: {
              maxHeight: 280,
              overflowY: 'auto',
            },
          },
        },
      },
      styleOverrides: {
        outlined: { borderRadius: 6 },
        select: {
          display: 'flex',
          alignItems: 'center',
          padding: '11px 14px',
          paddingRight: '36px !important',
          minHeight: 'auto',
          boxSizing: 'border-box',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          '&.MuiInputBase-inputSizeSmall': {
            padding: '8px 12px',
            paddingRight: '32px !important',
          },
        },
        icon: {
          right: 8,
          top: 'calc(50% - 12px)',
          color: isDark ? '#94A3B8' : '#64748B',
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        inputRoot: {
          padding: '4px 10px !important',
        },
        popupIndicator: {
          color: isDark ? '#94A3B8' : '#64748B',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 600, borderRadius: 6 },
        sizeSmall: { height: 24, borderRadius: 4 },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: isDark ? vyora.navyMid : '#F8FAFC',
          '& .MuiTableCell-head': {
            fontWeight: 700,
            fontSize: '0.72rem',
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            color: isDark ? '#94A3B8' : '#64748B',
            backgroundColor: isDark ? vyora.navyMid : '#F8FAFC',
            borderBottom: `1px solid ${hairline}`,
            zIndex: 2,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: hairline,
          paddingTop: 14,
          paddingBottom: 14,
        },
        head: {
          backgroundColor: isDark ? vyora.navyMid : '#F8FAFC',
          fontWeight: 700,
          color: isDark ? '#94A3B8' : '#64748B',
        },
        stickyHeader: {
          backgroundColor: isDark ? vyora.navyMid : '#F8FAFC',
          zIndex: 3,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: `background-color 160ms ${ease}`,
          '&.MuiTableRow-hover:hover': {
            backgroundColor: isDark ? alpha(vyora.aqua, 0.06) : alpha(vyora.teal, 0.05),
          },
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          maxWidth: '100%',
          minWidth: 0,
          overflowX: 'auto',
          overscrollBehaviorX: 'contain',
          WebkitOverflowScrolling: 'touch',
          borderRadius: 8,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 10,
          border: `1px solid ${hairline}`,
          backgroundImage: 'none',
          '@media (max-width: 600px)': {
            margin: 16,
            maxWidth: 'calc(100vw - 32px)',
          },
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: { fontWeight: 650, fontSize: '1.12rem', paddingBottom: 8 },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: { padding: '12px 24px 20px' },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundImage: 'none',
          paddingTop: 'var(--safe-area-top, env(safe-area-inset-top, 0px))',
        },
      },
    },
    MuiMenu: {
      defaultProps: {
        disableScrollLock: false,
      },
      styleOverrides: {
        paper: {
          borderRadius: 8,
          border: `1px solid ${hairline}`,
          boxShadow: isDark ? '0 16px 40px rgba(0,0,0,0.45)' : '0 16px 40px rgba(13,19,40,0.12)',
          maxHeight: 280,
          overflowY: 'auto',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: { borderRadius: 6, margin: '2px 6px', paddingInline: 12 },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: '0.75rem',
          borderRadius: 6,
          padding: '6px 10px',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 8, alignItems: 'center' },
        filled: { boxShadow: '0 10px 24px rgba(13,19,40,0.16)' },
      },
    },
    MuiSnackbar: {
      styleOverrides: {
        root: { zIndex: 1400 },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backdropFilter: 'blur(16px)',
          backgroundImage: 'none',
          paddingTop: 'var(--safe-area-top, env(safe-area-inset-top, 0px))',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          '&.Mui-selected': {
            backgroundColor: isDark ? vyora.aqua : vyora.teal,
            color: isDark ? vyora.navy : '#FFFFFF',
            '&:hover': {
              backgroundColor: isDark ? '#5EEAD4' : vyora.tealDark,
              color: isDark ? vyora.navy : '#FFFFFF',
            },
            '& .MuiListItemIcon-root': {
              color: isDark ? vyora.navy : '#FFFFFF',
            },
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: { height: 3, borderRadius: 2, backgroundColor: isDark ? vyora.aqua : vyora.teal },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: { minHeight: 46, fontWeight: 600 },
      },
    },
    MuiSkeleton: {
      styleOverrides: {
        root: { borderRadius: 6 },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: { fontWeight: 600 },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: { borderColor: hairline },
      },
    },
    MuiBottomNavigation: {
      styleOverrides: {
        root: {
          borderTop: `1px solid ${hairline}`,
          backgroundImage: 'none',
        },
      },
    },
    MuiBottomNavigationAction: {
      defaultProps: {
        disableRipple: true,
        disableTouchRipple: true,
      },
      styleOverrides: {
        root: {
          minWidth: 64,
          transition: 'none',
          WebkitTapHighlightColor: 'transparent',
          '& .MuiTouchRipple-root': {
            display: 'none',
          },
          '&.Mui-selected': {
            transition: 'none',
          },
        },
        label: {
          fontSize: '0.7rem',
          fontWeight: 600,
          transition: 'none',
          '&.Mui-selected': {
            fontSize: '0.7rem',
            transition: 'none',
          },
        },
      },
    },
    MuiPaginationItem: {
      styleOverrides: {
        root: { borderRadius: 6 },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: { borderRadius: 99, height: 6 },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: { padding: 8 },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: { borderRadius: 4 },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: { fontWeight: 600 },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: '64px !important',
          '@media (max-width:600px)': { minHeight: '56px !important', paddingLeft: 12, paddingRight: 8 },
        },
      },
    },
  };
}

export const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: vyora.teal,
      light: vyora.aqua,
      dark: vyora.tealDark,
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: vyora.purple,
      light: '#818CF8',
      dark: '#4F46E5',
      contrastText: '#FFFFFF',
    },
    background: {
      default: vyora.canvas,
      paper: vyora.paper,
    },
    success: { main: vyora.success },
    warning: { main: vyora.warning },
    error: { main: vyora.danger },
    info: { main: vyora.info },
    divider: vyora.hairlineLight,
    text: {
      primary: vyora.navy,
      secondary: vyora.muted,
    },
    action: {
      hover: alpha(vyora.teal, 0.06),
      selected: alpha(vyora.teal, 0.1),
    },
  },
  typography: sharedTypography,
  shape: sharedShape,
  components: componentOverrides('light'),
});

export const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: vyora.aqua,
      light: '#5EEAD4',
      dark: vyora.teal,
      contrastText: vyora.navy,
    },
    secondary: {
      main: '#818CF8',
      light: '#A5B4FC',
      dark: vyora.purple,
      contrastText: '#FFFFFF',
    },
    background: {
      default: vyora.navy,
      paper: vyora.navyMid,
    },
    success: { main: '#34D399' },
    warning: { main: '#FBBF24' },
    error: { main: '#F87171' },
    info: { main: '#38BDF8' },
    divider: vyora.hairlineDark,
    text: {
      primary: '#F8FAFC',
      secondary: '#94A3B8',
    },
    action: {
      hover: alpha(vyora.aqua, 0.08),
      selected: alpha(vyora.aqua, 0.12),
    },
  },
  typography: sharedTypography,
  shape: sharedShape,
  components: componentOverrides('dark'),
});
